import { useMemo } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
} from "recharts";
import { TrendingUp } from "lucide-react";

import { formatCRC } from "../../lib/api";

const DAY_LABELS = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];

const startOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
};

const buildBuckets = (txs, dateRange) => {
    // Only verified transactions count towards the chart total. Failed,
    // pending or under-manual-review tx are excluded from the sum.
    const verified = (txs || []).filter((t) => t.status === "verified");

    if (dateRange === "today") {
        // 24 hour buckets, only show working hours when possible
        const buckets = Array.from({ length: 24 }, (_, h) => ({
            key: `${h}`,
            label: `${String(h).padStart(2, "0")}h`,
            amount: 0,
            count: 0,
        }));
        const today = startOfDay(new Date()).getTime();
        verified.forEach((t) => {
            const d = new Date(t.created_at);
            if (startOfDay(d).getTime() !== today) return;
            const h = d.getHours();
            buckets[h].amount += Number(t.amount || 0);
            buckets[h].count += 1;
        });
        return buckets;
    }

    if (dateRange === "week") {
        const buckets = [];
        for (let i = 6; i >= 0; i--) {
            const d = new Date();
            d.setDate(d.getDate() - i);
            const start = startOfDay(d);
            buckets.push({
                key: start.toISOString(),
                ts: start.getTime(),
                label: DAY_LABELS[start.getDay()],
                amount: 0,
                count: 0,
            });
        }
        const byTs = new Map(buckets.map((b) => [b.ts, b]));
        verified.forEach((t) => {
            const ts = startOfDay(new Date(t.created_at)).getTime();
            const b = byTs.get(ts);
            if (b) {
                b.amount += Number(t.amount || 0);
                b.count += 1;
            }
        });
        return buckets;
    }

    // month / all: group by day for last 30 days
    const buckets = [];
    for (let i = 29; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const start = startOfDay(d);
        buckets.push({
            key: start.toISOString(),
            ts: start.getTime(),
            label: `${start.getDate()}/${start.getMonth() + 1}`,
            amount: 0,
            count: 0,
        });
    }
    const byTs = new Map(buckets.map((b) => [b.ts, b]));
    verified.forEach((t) => {
        const ts = startOfDay(new Date(t.created_at)).getTime();
        const b = byTs.get(ts);
        if (b) {
            b.amount += Number(t.amount || 0);
            b.count += 1;
        }
    });
    return buckets;
};

const formatCompactCRC = (v) => {
    if (v >= 1_000_000) return `₡${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `₡${Math.round(v / 1000)}k`;
    return `₡${v}`;
};

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div className="bg-[#022C22] text-white rounded-xl px-4 py-3 shadow-xl border border-[#064E3B]" data-testid="chart-tooltip">
            <p className="font-display font-medium text-sm">{label}</p>
            <p className="text-[#A7F3D0] font-mono text-base font-semibold mt-1.5">{formatCRC(d.amount)}</p>
            <p className="text-white/70 text-xs mt-0.5">
                {d.count} {d.count === 1 ? "transacción" : "transacciones"}
            </p>
        </div>
    );
};

const RANGE_LABEL = {
    today: "Por hora · hoy",
    week: "Por día · últimos 7 días",
    month: "Por día · últimos 30 días",
    all: "Por día · últimos 30 días",
};

export default function SalesChart({ transactions, dateRange }) {
    const data = useMemo(
        () => buildBuckets(transactions || [], dateRange),
        [transactions, dateRange]
    );

    const totalAmount = useMemo(
        () => data.reduce((acc, b) => acc + b.amount, 0),
        [data]
    );
    const totalCount = useMemo(
        () => data.reduce((acc, b) => acc + b.count, 0),
        [data]
    );

    const hasData = totalCount > 0;

    return (
        <div className="card-clean p-5 md:p-7" data-testid="sales-chart-card">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
                <div>
                    <div className="flex items-center gap-2.5">
                        <div className="h-9 w-9 rounded-xl bg-[#D1FAE5] flex items-center justify-center">
                            <TrendingUp className="w-4.5 h-4.5 text-[#065F46]" strokeWidth={2} />
                        </div>
                        <div>
                            <h2 className="font-display text-xl md:text-2xl font-medium text-[#022C22] leading-none">
                                Tendencia de ventas
                            </h2>
                            <p className="text-xs uppercase tracking-widest text-[#64748B] mt-1.5">
                                {RANGE_LABEL[dateRange] || RANGE_LABEL.all}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="text-right" data-testid="chart-totals">
                    <p className="text-xs uppercase tracking-widest text-[#64748B]">Total verificado del periodo</p>
                    <p className="font-display text-2xl font-semibold text-[#022C22] tracking-tight mt-0.5">
                        {formatCRC(totalAmount)}
                    </p>
                    <p className="text-xs text-[#64748B] mt-0.5">
                        {totalCount} {totalCount === 1 ? "transacción" : "transacciones"}
                    </p>
                </div>
            </div>

            <div className="h-[220px] md:h-[240px] w-full" data-testid="sales-chart-container">
                {hasData ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={data}
                            margin={{ top: 10, right: 4, left: -10, bottom: 0 }}
                            barCategoryGap="22%"
                        >
                            <CartesianGrid
                                vertical={false}
                                stroke="#E2E8F0"
                                strokeDasharray="3 6"
                            />
                            <XAxis
                                dataKey="label"
                                tickLine={false}
                                axisLine={false}
                                tick={{ fill: "#64748B", fontSize: 11, fontFamily: "Manrope" }}
                                interval={dateRange === "today" ? 2 : dateRange === "month" || dateRange === "all" ? 3 : 0}
                            />
                            <YAxis
                                tickLine={false}
                                axisLine={false}
                                tick={{ fill: "#94A3B8", fontSize: 11, fontFamily: "JetBrains Mono" }}
                                tickFormatter={formatCompactCRC}
                                width={60}
                            />
                            <Tooltip
                                cursor={{ fill: "rgba(16, 185, 129, 0.08)" }}
                                content={<CustomTooltip />}
                            />
                            <Bar
                                dataKey="amount"
                                fill="#10b981"
                                radius={[6, 6, 0, 0]}
                                maxBarSize={48}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="h-full flex flex-col items-center justify-center text-center" data-testid="chart-empty">
                        <div className="h-14 w-14 rounded-full bg-[#F8FAFC] flex items-center justify-center mb-3">
                            <TrendingUp className="w-6 h-6 text-[#64748B]" />
                        </div>
                        <p className="font-medium text-[#022C22]">Sin datos para este periodo</p>
                        <p className="text-sm text-[#64748B] max-w-xs mt-1">
                            Cambiá el filtro o esperá a que lleguen nuevas transacciones.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
